import { useState, useEffect } from 'react';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ScanLine, Camera, Search, Pill, AlertTriangle, 
  CheckCircle, Info, Loader2, Upload, History
} from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

const MedicineScanner = ({ user, setUser }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [commonMedicines, setCommonMedicines] = useState([]);
  const [scanHistory, setScanHistory] = useState([]);
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [interactionMedicines, setInteractionMedicines] = useState(['']);
  const [interactionResults, setInteractionResults] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [medicinesRes, historyRes] = await Promise.all([
        axios.get(`${API}/medicine-scanner/common-medicines`),
        axios.get(`${API}/medicine-scanner/scan-history`)
      ]);
      setCommonMedicines(medicinesRes.data.medicines || []);
      setScanHistory(historyRes.data.scans || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    try {
      const response = await axios.post(`${API}/medicine-scanner/search`, { query: searchQuery });
      setSearchResults(response.data.results || []);
      if (response.data.results.length === 0) {
        toast.info('No medicines found matching your search');
      }
    } catch (error) {
      toast.error('Search failed');
    }
  };

  const getMedicineInfo = async (medicineName) => {
    try {
      const response = await axios.get(`${API}/medicine-scanner/info/${medicineName}`);
      if (response.data.found) {
        setSelectedMedicine(response.data.medicine);
      } else {
        toast.error('Medicine details not found');
      }
    } catch (error) {
      toast.error('Failed to get medicine info');
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setScanning(true);
    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await axios.post(`${API}/medicine-scanner/scan`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setSelectedMedicine(response.data);
      toast.success(`Identified: ${response.data.medicine_name}`);
      loadData(); // Refresh scan history
    } catch (error) {
      toast.error('Failed to scan medicine. Please try again.');
    } finally {
      setScanning(false);
    }
  };

  const checkInteractions = async () => {
    const medicines = interactionMedicines.filter(m => m.trim());
    if (medicines.length < 2) {
      toast.error('Please enter at least 2 medicines to check interactions');
      return;
    }

    try {
      const response = await axios.post(`${API}/medicine-scanner/check-interactions`, { medicines });
      setInteractionResults(response.data);
    } catch (error) {
      toast.error('Failed to check interactions');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
        <Navigation user={user} setUser={setUser} />
        <div className="container mx-auto px-4 py-8 pt-24 flex justify-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      <Navigation user={user} setUser={setUser} />
      
      <div className="container mx-auto px-4 py-6 pt-24 max-w-4xl">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <ScanLine className="w-8 h-8 text-purple-600" />
            Medicine Scanner
          </h1>
          <p className="text-gray-600 mt-1">Scan, search, and learn about medicines</p>
        </div>

        <Tabs defaultValue="scan" className="space-y-6">
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="scan">Scan</TabsTrigger>
            <TabsTrigger value="search">Search</TabsTrigger>
            <TabsTrigger value="interactions">Interactions</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>

          {/* Scan Tab */}
          <TabsContent value="scan">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Camera className="w-5 h-5 text-purple-600" />
                  Scan Medicine
                </CardTitle>
                <CardDescription>Take a photo of a medicine to identify it</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <label className="block">
                    <div className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer hover:border-purple-400 transition-colors ${scanning ? 'border-purple-400 bg-purple-50' : 'border-gray-300'}`}>
                      {scanning ? (
                        <div className="flex flex-col items-center">
                          <Loader2 className="w-12 h-12 animate-spin text-purple-600 mb-3" />
                          <p className="text-purple-600 font-medium">Analyzing medicine...</p>
                        </div>
                      ) : (
                        <>
                          <Upload className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                          <p className="text-gray-600 font-medium">Click to upload or take photo</p>
                          <p className="text-sm text-gray-400 mt-1">Supports JPG, PNG</p>
                        </>
                      )}
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      onChange={handleImageUpload}
                      disabled={scanning}
                    />
                  </label>

                  {selectedMedicine && (
                    <div className="bg-white rounded-xl border p-6 space-y-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-xl font-bold text-gray-800">{selectedMedicine.medicine_name || selectedMedicine.name}</h3>
                          {selectedMedicine.generic_name && (
                            <p className="text-gray-500">{selectedMedicine.generic_name}</p>
                          )}
                        </div>
                        {selectedMedicine.confidence && (
                          <Badge className={selectedMedicine.confidence > 0.7 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                            {Math.round(selectedMedicine.confidence * 100)}% match
                          </Badge>
                        )}
                      </div>

                      {selectedMedicine.category && (
                        <Badge variant="outline">{selectedMedicine.category}</Badge>
                      )}

                      {selectedMedicine.uses?.length > 0 && (
                        <div>
                          <h4 className="font-medium text-gray-700 mb-2">Uses</h4>
                          <div className="flex flex-wrap gap-2">
                            {selectedMedicine.uses.map((use, i) => (
                              <Badge key={`item-${i}`} variant="secondary">{use}</Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {selectedMedicine.dosage && (
                        <div>
                          <h4 className="font-medium text-gray-700 mb-1">Dosage</h4>
                          <p className="text-gray-600">{selectedMedicine.dosage}</p>
                        </div>
                      )}

                      {selectedMedicine.side_effects?.length > 0 && (
                        <div>
                          <h4 className="font-medium text-gray-700 mb-2 flex items-center gap-1">
                            <AlertTriangle className="w-4 h-4 text-yellow-500" />
                            Side Effects
                          </h4>
                          <ul className="list-disc list-inside text-gray-600 text-sm">
                            {selectedMedicine.side_effects.map((effect, i) => (
                              <li key={`item-${i}`}>{effect}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {selectedMedicine.warnings?.length > 0 && (
                        <div className="bg-red-50 rounded-lg p-4">
                          <h4 className="font-medium text-red-700 mb-2 flex items-center gap-1">
                            <AlertTriangle className="w-4 h-4" />
                            Warnings
                          </h4>
                          <ul className="list-disc list-inside text-red-600 text-sm">
                            {selectedMedicine.warnings.map((warning, i) => (
                              <li key={`item-${i}`}>{warning}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Search Tab */}
          <TabsContent value="search">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="w-5 h-5 text-purple-600" />
                  Search Medicines
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2 mb-6">
                  <Input
                    placeholder="Search medicine name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                  <Button onClick={handleSearch}>
                    <Search className="w-4 h-4" />
                  </Button>
                </div>

                {searchResults.length > 0 ? (
                  <div className="space-y-3">
                    {searchResults.map((medicine) => (
                      <div
                        key={medicine.id}
                        className="p-4 rounded-lg border hover:border-purple-300 cursor-pointer transition-colors"
                        onClick={() => setSelectedMedicine(medicine)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium">{medicine.name}</h4>
                            <p className="text-sm text-gray-500">{medicine.generic_name}</p>
                          </div>
                          <Badge variant="outline">{medicine.category}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div>
                    <h4 className="font-medium text-gray-700 mb-3">Common Medicines</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {commonMedicines.map((medicine) => (
                        <button
                          key={medicine.id}
                          className="p-3 rounded-lg border text-left hover:border-purple-300 transition-colors"
                          onClick={() => getMedicineInfo(medicine.id)}
                        >
                          <Pill className="w-5 h-5 text-purple-500 mb-1" />
                          <p className="font-medium text-sm">{medicine.name}</p>
                          <p className="text-xs text-gray-500 truncate">{medicine.category}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Interactions Tab */}
          <TabsContent value="interactions">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-500" />
                  Drug Interaction Checker
                </CardTitle>
                <CardDescription>Check if your medicines interact with each other</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {interactionMedicines.map((med, index) => (
                    <div key={`item-${index}`} className="flex gap-2">
                      <Input
                        placeholder={`Medicine ${index + 1}`}
                        value={med}
                        onChange={(e) => {
                          const updated = [...interactionMedicines];
                          updated[index] = e.target.value;
                          setInteractionMedicines(updated);
                        }}
                      />
                      {index === interactionMedicines.length - 1 && (
                        <Button
                          variant="outline"
                          onClick={() => setInteractionMedicines([...interactionMedicines, ''])}
                        >
                          +
                        </Button>
                      )}
                    </div>
                  ))}
                  
                  <Button onClick={checkInteractions} className="w-full">
                    Check Interactions
                  </Button>

                  {interactionResults && (
                    <div className="mt-6 space-y-4">
                      {interactionResults.interactions.length > 0 ? (
                        <div className="bg-orange-50 rounded-lg p-4">
                          <h4 className="font-medium text-orange-800 mb-3 flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5" />
                            Interactions Found ({interactionResults.interactions_found})
                          </h4>
                          {interactionResults.interactions.map((interaction, i) => (
                            <div key={`item-${i}`} className="bg-white rounded p-3 mb-2">
                              <p className="font-medium">{interaction.drug1} + {interaction.drug2}</p>
                              <p className="text-sm text-gray-600">{interaction.description}</p>
                              <Badge className="mt-1 bg-orange-100 text-orange-800">{interaction.severity}</Badge>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="bg-green-50 rounded-lg p-4">
                          <h4 className="font-medium text-green-800 flex items-center gap-2">
                            <CheckCircle className="w-5 h-5" />
                            No Major Interactions Found
                          </h4>
                          <p className="text-sm text-green-700 mt-1">{interactionResults.recommendation}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="w-5 h-5 text-purple-600" />
                  Scan History
                </CardTitle>
              </CardHeader>
              <CardContent>
                {scanHistory.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <ScanLine className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No scan history yet</p>
                    <p className="text-sm">Scan a medicine to see it here</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {scanHistory.map((scan, index) => (
                      <div
                        key={`item-${index}`}
                        className="p-4 rounded-lg border hover:border-purple-300 cursor-pointer"
                        onClick={() => setSelectedMedicine(scan)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium">{scan.medicine_name}</h4>
                            <p className="text-sm text-gray-500">
                              {new Date(scan.scanned_at).toLocaleDateString()}
                            </p>
                          </div>
                          {scan.confidence && (
                            <Badge variant="outline">{Math.round(scan.confidence * 100)}%</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default MedicineScanner;
